/**
 * 점주 콘솔 세션 컨텍스트 — backend 실세션 기반.
 *
 * 기본(live 모드): GET /api/v1/owner-console/session 응답을 세션으로 사용.
 *   - 미로그인(401) → api client 인터셉터가 /login 으로 보냄 (기존 관리자 정책)
 *   - 403 (회원 토큰/매장 미연결) → status 'denied' → 권한 없음 화면
 *   - 그 외 실패 → production 은 status 'error', dev/test 는 mock fallback
 *
 * mock 모드 (테스트/dev 전용 — production 빌드에서는 진입 경로 자체가 제거됨):
 *   - 테스트: initialSessionKey prop
 *   - dev: 세션 전환기(SessionSwitcher)로 전환
 *
 * ⚠️ 세션의 permissions 로 하는 메뉴 숨김/라우트 차단은 UX용이다.
 * 실제 보안은 backend 의 @Roles + @RequirePermissions 가드가 담당한다.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MOCK_SESSIONS, DEFAULT_SESSION_KEY } from '../mock/sessions';
import { fetchOwnerConsoleSession } from '../api/session';

// CRA 빌드 시 상수로 치환 → production 번들에서 mock 분기가 제거된다
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const MASTER_STORE_STORAGE_KEY = 'ownerConsoleV2.masterStoreId';
const MASTER_RECENT_STORES_KEY = 'ownerConsoleV2.masterRecentStoreIds';
const MASTER_RECENT_LIMIT = 5;

/**
 * QA 재현 링크 — URL 의 ?storeId= 가 저장값보다 우선한다.
 * master 가 "이 화면 그대로" 링크를 공유하면 상대도 같은 매장 기준으로 열린다.
 */
const readStoreIdFromUrl = () => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('storeId');
  } catch {
    return null;
  }
};

/** 주소창의 storeId 를 현재 선택과 동기화 (history 를 더럽히지 않도록 replace) */
const syncStoreIdToUrl = (storeId) => {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    if (storeId) url.searchParams.set('storeId', storeId);
    else url.searchParams.delete('storeId');
    window.history.replaceState({}, '', url);
  } catch {
    /* URL 조작 실패는 무시 — 기능에 영향 없음 */
  }
};

const readRecentStoreIds = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(MASTER_RECENT_STORES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const pushRecentStoreId = (storeId) => {
  if (typeof window === 'undefined' || !storeId) return readRecentStoreIds();
  const next = [storeId, ...readRecentStoreIds().filter((id) => id !== storeId)].slice(
    0,
    MASTER_RECENT_LIMIT,
  );
  try {
    localStorage.setItem(MASTER_RECENT_STORES_KEY, JSON.stringify(next));
  } catch {
    /* 저장 실패는 무시 */
  }
  return next;
};

const SessionContext = createContext(null);

const readStoredMasterStoreId = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(MASTER_STORE_STORAGE_KEY);
};

const persistMasterStoreId = (storeId) => {
  if (typeof window === 'undefined') return;
  if (storeId) localStorage.setItem(MASTER_STORE_STORAGE_KEY, storeId);
  else localStorage.removeItem(MASTER_STORE_STORAGE_KEY);
};

export function SessionProvider({ children, initialSessionKey = null }) {
  const startInMock = !IS_PRODUCTION && initialSessionKey && Boolean(MOCK_SESSIONS[initialSessionKey]);

  // mode: 'live' = backend 세션, 'mock' = MOCK_SESSIONS (dev/test 전용)
  const [mode, setMode] = useState(startInMock ? 'mock' : 'live');
  const [mockKey, setMockKey] = useState(startInMock ? initialSessionKey : DEFAULT_SESSION_KEY);
  const [liveSession, setLiveSession] = useState(null);
  // URL(공유 링크) → localStorage(직전 선택) 순으로 초기 매장을 정한다
  const [selectedStoreId, setSelectedStoreId] = useState(
    () => readStoreIdFromUrl() || readStoredMasterStoreId(),
  );
  const [recentStoreIds, setRecentStoreIds] = useState(readRecentStoreIds);
  // 'loading' | 'ready' | 'denied' | 'error'
  const [status, setStatus] = useState(startInMock ? 'ready' : 'loading');
  const [errorMessage, setErrorMessage] = useState('');

  const loadLiveSession = useCallback(
    async (storeId = selectedStoreId, allowStoredFallback = true) => {
      setStatus('loading');
      try {
        const session = await fetchOwnerConsoleSession(storeId);
        setLiveSession(session);
        if (session?.user?.role === 'master') {
          setSelectedStoreId(session.store.id);
          persistMasterStoreId(session.store.id);
          setRecentStoreIds(pushRecentStoreId(session.store.id));
          // 서버가 실제로 고른 매장으로 주소창을 맞춘다 (그대로 공유하면 재현된다)
          syncStoreIdToUrl(session.store.id);
        }
        setStatus('ready');
      } catch (err) {
        if (storeId && allowStoredFallback && [400, 403].includes(err?.response?.status)) {
          persistMasterStoreId(null);
          setSelectedStoreId(null);
          syncStoreIdToUrl(null);
          return loadLiveSession(null, false);
        }

        if (err?.response?.status === 403) {
          // 로그인은 됐지만 점주 콘솔 대상 계정이 아님 (master/회원/매장 미연결 등)
          setErrorMessage(err.response?.data?.message || '점주 콘솔을 사용할 수 없는 계정입니다.');
          setStatus('denied');
        } else if (!IS_PRODUCTION) {
          // dev/test fallback: backend 없이 UI 작업할 때만 mock 으로 대체
          // eslint-disable-next-line no-console
          console.warn('[owner-console-v2] 세션 로딩 실패 — dev mock 세션으로 대체합니다:', err?.message);
          setMode('mock');
          setStatus('ready');
        } else {
          setErrorMessage('세션을 불러오지 못했어요. 네트워크 상태를 확인해 주세요.');
          setStatus('error');
        }
      }
    },
    [selectedStoreId],
  );

  useEffect(() => {
    if (mode === 'live' && !liveSession) {
      loadLiveSession();
    }
    // status 는 의도적으로 deps 에서 제외 — 실패(error/denied) 시 자동 재시도 루프 방지
  }, [mode, liveSession, loadLiveSession]);

  // dev/test 전용 세션 전환 — production 에서는 no-op (분기 자체가 빌드에서 제거)
  const switchToMock = useCallback((key) => {
    if (IS_PRODUCTION || !MOCK_SESSIONS[key]) return;
    setMockKey(key);
    setMode('mock');
    setStatus('ready');
  }, []);

  const switchToLive = useCallback(() => {
    setMode('live');
    if (!liveSession) setStatus('loading');
    else setStatus('ready');
  }, [liveSession]);

  const selectStore = useCallback(
    (storeId) => {
      if (!storeId || storeId === liveSession?.store?.id) return;
      persistMasterStoreId(storeId);
      setSelectedStoreId(storeId);
      setLiveSession(null);
      setMode('live');
      loadLiveSession(storeId, false);
    },
    [liveSession?.store?.id, loadLiveSession],
  );

  const session = mode === 'mock' ? MOCK_SESSIONS[mockKey] : liveSession;

  const value = useMemo(
    () => ({
      session,
      status,
      errorMessage,
      mode,
      mockKey,
      mockSessions: MOCK_SESSIONS,
      storeOptions: session?.storeOptions ?? [],
      selectedStoreId: session?.store?.id ?? selectedStoreId,
      /** master 매장 선택기에서 '최근 본 매장'을 위로 올리는 데 쓴다 */
      recentStoreIds,
      /** master QA 모드 여부 — 서버 플래그 우선, mock 세션은 role 로 판정 */
      masterQaMode: session?.masterQaMode ?? session?.user?.role === 'master',
      switchToMock,
      switchToLive,
      selectStore,
      retry: loadLiveSession,
    }),
    [
      session,
      status,
      errorMessage,
      mode,
      mockKey,
      selectedStoreId,
      recentStoreIds,
      switchToMock,
      switchToLive,
      selectStore,
      loadLiveSession,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession은 SessionProvider 하위에서만 사용할 수 있습니다.');
  }
  return ctx;
}
